import { useCallback, useEffect, useState } from 'react'

import { useGuideContext } from 'react-provider'
import BookListItem from '@/components/BookListItem'
import CardLayout from '@/components/CardLayout'
import { useCustomNavigate, useVisibilityReport } from '@/hooks'
import { isSuccess } from '@/axios'
import { getDiscoverBookList, IDiscoverBookListResponse } from '@/api'
import { cn, handleResourceClickTrack, handleResourceDisplayTrack } from '@/utils'
import SkeletonState from '@/components/CommonState/SkeletonState'

interface PropsIn {
  className?: string
}

const DiscoverBookList = (props: PropsIn) => {
  const { className } = props
  const { customNavigator } = useCustomNavigate()
  const [data, setData] = useState({} as IDiscoverBookListResponse)
  const [isLoading, setIsLoading] = useState(true)
  const { reportReady } = useGuideContext()

  const getBookList = useCallback(async () => {
    setIsLoading(true)
    const resp = await getDiscoverBookList()
    setIsLoading(false)
    if (isSuccess(resp)) {
      return setData(resp.data)
    }
    setData({ list: [] })
  }, [])

  const handleMore = () => {
    customNavigator('/bookList')
  }

  useEffect(() => {
    getBookList()
  }, [])

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        getBookList()
      }
    }

    window.addEventListener('pageshow', handlePageShow)

    return () => {
      window.removeEventListener('pageshow', handlePageShow)
    }
  }, [])

  useVisibilityReport('bookList', () => {
    handleResourceDisplayTrack({
      booklist_display: true,
    })
  })

  if (isLoading) {
    return (
      <CardLayout rootClassName={cn(className)} title='Booklist'>
        <SkeletonState type={['bookListItem']} commentCount={2} />
      </CardLayout>
    )
  }

  return (
    <>
      {data?.list?.length ? (
        <CardLayout
          rootClassName={cn(className)}
          title='Booklist'
          ref={() => {
            reportReady('bookListBox')
          }}
          extra={
            <span className={cn('cursor-pointer font-geologica text-[26px] font-light text-color-white-trans70')} onClick={handleMore}>
              More
            </span>
          }
          id='bookList'
        >
          <div className={cn('flex flex-col px-[30px] md:flex-row md:px-0')}>
            {data?.list.map((item, index) => {
              const isFirst = index === 0
              const isSecond = index === 1
              const hasMultiple = data.list.length > 1

              return (
                <BookListItem
                  key={`${item.cmty_id}-${index}`}
                  className={cn('cursor-pointer', isFirst && 'mb-[22px] md:mb-0', isSecond && 'md:ml-[30px]', hasMultiple ? 'md:w-1/2' : 'md:w-full')}
                  afterOnClick={() => {
                    handleResourceClickTrack({
                      booklist_enter: item.cmty_id,
                      resource_id: '',
                      resource_genre: '',
                      resource_tag_id: '',
                      button: '1',
                    })
                  }}
                  data={item}
                />
              )
            })}
          </div>
        </CardLayout>
      ) : null}
    </>
  )
}

export default DiscoverBookList
